import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { createRouter } from '@tanstack/react-router'
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query'

import { createApiUtils, createTRPCClient, isTRPCClientError } from './core/trpc'
import { routeTree } from './routeTree.gen'
import { removeJwt } from './utils/jwt'

export function getRouter() {
    const isSSR = typeof window === 'undefined'

    /**
     * A token the API has rejected is worse than no token: every authed query keeps failing and the
     * guard still waves the user through, because it only checks that a token exists. Clearing it
     * and bouncing to the login form is the only way back.
     *
     * Client-only: during SSR there is no location to replace, and a 401 there is expected — the
     * server never has a token to send in the first place.
     */
    const onUnauthorized = (error: unknown) => {
        if (isSSR || typeof window === 'undefined') return
        if (!isTRPCClientError(error) || error.data?.code !== 'UNAUTHORIZED') return

        removeJwt()

        const redirect = `${window.location.pathname}${window.location.search}`
        window.location.replace(`/login?redirect=${encodeURIComponent(redirect)}`)
    }

    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                refetchInterval: isSSR ? false : 60000,
                staleTime: isSSR ? Infinity : 0,
            },
        },
        queryCache: new QueryCache({ onError: onUnauthorized }),
        mutationCache: new MutationCache({ onError: onUnauthorized }),
    })

    const trpcClient = createTRPCClient()
    const apiUtils = createApiUtils({ queryClient, client: trpcClient })

    const router = createRouter({
        routeTree,
        defaultPreload: 'intent',
        scrollRestoration: true,
        /**
         * The canonical url form, matching every url the site already serves. Stating it rather
         * than relying on the default is what lets the cms emit canonical tags, hreflang
         * alternates and redirect targets in exactly the form the router will resolve.
         */
        trailingSlash: 'never',
        context: { queryClient, trpcClient, apiUtils },
    })

    setupRouterSsrQueryIntegration({ router, queryClient })

    return router
}

declare module '@tanstack/react-router' {
    interface Register {
        router: ReturnType<typeof getRouter>
    }
}
